import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@monigo/ui';
import { Logo } from '../Brand/Logo.js';
import { LanguageToggle } from '../LanguageToggle.js';
import { useSession } from '../../lib/store/session.js';

const navLinks = [
  { to: '/app', key: 'nav.home', icon: HomeIcon },
  { to: '/transfers', key: 'nav.transfers', icon: ActivityIcon },
  { to: '/beneficiaries', key: 'nav.beneficiaries', icon: RecipientsIcon },
  { to: '/settings', key: 'nav.settings', icon: SettingsIcon },
] as const;

export function AppShell() {
  const { t } = useTranslation();
  const { user, signOut } = useSession();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background text-text">
      <header className="sticky top-0 z-20 border-b border-border bg-background/95 backdrop-blur">
        <div className="container flex flex-col gap-3 py-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center justify-between gap-3">
            <NavLink to="/app" aria-label={t('common.appName')}>
              <Logo className="text-xl sm:text-2xl" />
            </NavLink>
            <div className="flex items-center gap-2 md:hidden">
              <LanguageToggle />
            </div>
          </div>
          <nav className="hidden gap-3 text-sm lg:flex" aria-label={t('common.appName')}>
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === '/app'}
                className={({ isActive }) =>
                  cn(
                    'rounded-pill px-4 py-2 text-text-muted transition hover:text-text',
                    isActive && 'bg-surface-elevated text-primary',
                  )
                }
              >
                {t(l.key)}
              </NavLink>
            ))}
          </nav>
          <div className="hidden items-center gap-2 md:flex">
            <LanguageToggle />
            {user ? (
              <button
                type="button"
                onClick={async () => {
                  await signOut();
                  navigate('/login');
                }}
                className="rounded-pill border border-border bg-surface px-3 py-2 text-sm font-semibold text-text-muted lg:px-4"
              >
                {t('settings.logout')}
              </button>
            ) : null}
          </div>
        </div>
      </header>

      <main className="container pb-24 pt-6 md:pb-8">
        <Outlet />
      </main>

      <nav
        className="fixed bottom-0 left-0 right-0 z-30 border-t border-border bg-surface md:hidden"
        aria-label="Mobile navigation"
      >
        <div className="grid grid-cols-4 text-[11px] font-medium">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === '/app'}
              className={({ isActive }) =>
                cn(
                  'flex flex-col items-center gap-1 px-2 py-2 text-center text-text-muted transition',
                  isActive && 'text-primary',
                )
              }
            >
              <l.icon />
              {t(l.key)}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}

function HomeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path
        d="M3 7.75L9 3L15 7.75V14C15 14.5523 14.5523 15 14 15H11V10.75H7V15H4C3.44772 15 3 14.5523 3 14V7.75Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ActivityIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path
        d="M3 9H6L7.75 5L10.25 13L12 9H15"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function RecipientsIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path
        d="M9 9.25C10.5188 9.25 11.75 8.01878 11.75 6.5C11.75 4.98122 10.5188 3.75 9 3.75C7.48122 3.75 6.25 4.98122 6.25 6.5C6.25 8.01878 7.48122 9.25 9 9.25Z"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M4.5 14.25C5.16667 12.75 6.66667 11.75 9 11.75C11.3333 11.75 12.8333 12.75 13.5 14.25"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function SettingsIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path
        d="M9 11.25C10.2426 11.25 11.25 10.2426 11.25 9C11.25 7.75736 10.2426 6.75 9 6.75C7.75736 6.75 6.75 7.75736 6.75 9C6.75 10.2426 7.75736 11.25 9 11.25Z"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M14.25 9C14.25 8.59 14.21 8.19 14.12 7.81L15.75 6.56L14.19 3.81L12.25 4.5C11.64 4.01 10.92 3.65 10.12 3.47L9.75 1.5H8.25L7.88 3.47C7.08 3.65 6.36 4.01 5.75 4.5L3.81 3.81L2.25 6.56L3.88 7.81C3.79 8.19 3.75 8.59 3.75 9C3.75 9.41 3.79 9.81 3.88 10.19L2.25 11.44L3.81 14.19L5.75 13.5C6.36 13.99 7.08 14.35 7.88 14.53L8.25 16.5H9.75L10.12 14.53C10.92 14.35 11.64 13.99 12.25 13.5L14.19 14.19L15.75 11.44L14.12 10.19C14.21 9.81 14.25 9.41 14.25 9Z"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
    </svg>
  );
}
